import { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'baseui/snackbar'
import { showSnackbar, SNACKBAR_TYPES } from '@/shared/tools/notifications/notification'
import { fidelityMaximizerModuleActions } from './FidelityMaximizerSagas'
import {
  selectConfig,
  selectContactConfig,
  selectFidelityMaximizerState,
} from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSelectors'
import { updateFidelityMaximizer } from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSlice'
import useFidelityMaximizerEligibility from './useFidelityMaximizerEligibility'
import useFidelityActionRealtimeSync from './useFidelityActionRealtimeSync'

const useFidelityMaximizer = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { enqueue } = useSnackbar()
  const { contactId } = useSelector(selectConfig)
  const { contractLoaded } = useSelector(selectContactConfig)
  const fidelityMaximizer = useSelector(selectFidelityMaximizerState)
  const { loading, loaded, enabled, checklist, isPdlEligible, scenarioKey, actionKey, actionAvailable, actionLoading } =
    fidelityMaximizer

  const isEligibleAgent = useFidelityMaximizerEligibility()

  const handleActionCompleted = useCallback(
    ({ success, message }) => {
      dispatch(updateFidelityMaximizer({ actionLoading: false }))
      showSnackbar(enqueue, {
        type: success ? SNACKBAR_TYPES.SUCCESS : SNACKBAR_TYPES.ERROR,
        message: message || (success ? 'Action envoyée avec succès.' : "Erreur lors de l'envoi."),
      })
    },
    [dispatch, enqueue],
  )

  useFidelityActionRealtimeSync(contactId, isEligibleAgent, handleActionCompleted)

  useEffect(() => {
    if (!contractLoaded || !contactId || !isEligibleAgent || loaded || loading) {
      return
    }

    dispatch({ type: fidelityMaximizerModuleActions.FIDELITY_MAXIMIZER_LOAD_STATUS, contactId })
  }, [contractLoaded, contactId, isEligibleAgent, loaded, loading])

  const triggerAction = () => {
    if (!actionKey || !actionAvailable || actionLoading) {
      return
    }

    dispatch({
      type: fidelityMaximizerModuleActions.FIDELITY_MAXIMIZER_TRIGGER_ACTION,
      contactId,
      action: actionKey,
      enqueue,
    })
  }

  return {
    t,
    hidden: !isEligibleAgent || !loaded || !enabled,
    loading,
    checklist,
    isPdlEligible,
    scenarioKey,
    actionAvailable,
    actionLoading,
    triggerAction,
  }
}

export default useFidelityMaximizer
